// src/components/attendance/MarkAttendance.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';

const MarkAttendance = ({ premises, onSubmit, onCancel, userType }) => {
    const { user } = useAuth();
    const [formData, setFormData] = useState({
        premises_id: '',
        attendance_type: 'manual',
        latitude: null,
        longitude: null,
        notes: ''
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [locationLoading, setLocationLoading] = useState(false);
    const [locationError, setLocationError] = useState(null);

    useEffect(() => {
        // Auto-select first premises if only one available
        if (premises.length === 1) {
            setFormData(prev => ({
                ...prev,
                premises_id: premises[0].id
            }));
        }

        // Get current location
        getCurrentLocation();
    }, [premises]);

    const getCurrentLocation = () => {
        if (!navigator.geolocation) {
            setLocationError('Geolocation is not supported by this browser');
            return;
        }

        setLocationLoading(true);
        navigator.geolocation.getCurrentPosition(
            (position) => {
                setFormData(prev => ({
                    ...prev,
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude
                }));
                setLocationLoading(false);
                setLocationError(null);
            },
            (error) => {
                setLocationError('Unable to retrieve your location');
                setLocationLoading(false);
                console.error('Geolocation error:', error);
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 600000 // 10 minutes
            }
        );
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.premises_id) {
            newErrors.premises_id = 'Please select a premises';
        }

        if (!formData.attendance_type) {
            newErrors.attendance_type = 'Please select attendance type';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        try {
            await onSubmit(formData);
        } catch (error) {
            console.error('Mark attendance error:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="mark-attendance-form">
            {/* Location Status */}
            <div className="location-status">
                <div className="location-header">
                    <h4>
                        <i className="fas fa-map-marker-alt"></i>
                        Location Status
                    </h4>
                    <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={getCurrentLocation}
                        disabled={locationLoading}
                    >
                        {locationLoading ? (
                            <div className="spinner"></div>
                        ) : (
                            <i className="fas fa-sync-alt"></i>
                        )}
                        Refresh Location
                    </button>
                </div>

                {locationLoading && (
                    <div className="location-loading">
                        <i className="fas fa-spinner fa-spin"></i>
                        Getting your location...
                    </div>
                )}

                {locationError && (
                    <div className="location-error">
                        <i className="fas fa-exclamation-triangle"></i>
                        {locationError}
                    </div>
                )}

                {formData.latitude && formData.longitude && (
                    <div className="location-success">
                        <i className="fas fa-check-circle"></i>
                        Location detected: {formData.latitude.toFixed(6)}, {formData.longitude.toFixed(6)}
                    </div>
                )}
            </div>

            {/* Premises Selection */}
            <div className="form-group">
                <label htmlFor="premises_id" className="form-label">
                    Select Premises <span className="required">*</span>
                </label>
                <select
                    id="premises_id"
                    name="premises_id"
                    value={formData.premises_id}
                    onChange={handleChange}
                    className={`form-input ${errors.premises_id ? 'error' : ''}`}
                    required
                >
                    <option value="">Choose premises...</option>
                    {premises.map(premise => (
                        <option key={premise.id} value={premise.id}>
                            {premise.premises_name}
                        </option>
                    ))}
                </select>
                {errors.premises_id && <span className="form-error">{errors.premises_id}</span>}
            </div>

            {/* Attendance Type */}
            <div className="form-group">
                <label htmlFor="attendance_type" className="form-label">
                    Attendance Type <span className="required">*</span>
                </label>
                <select
                    id="attendance_type"
                    name="attendance_type"
                    value={formData.attendance_type}
                    onChange={handleChange}
                    className={`form-input ${errors.attendance_type ? 'error' : ''}`}
                    required
                >
                    <option value="manual">Manual Entry</option>
                    <option value="face_recognition">Face Recognition</option>
                    <option value="offsite">Offsite Work</option>
                    {userType !== 'staff' && <option value="auto_checkout">Auto Checkout</option>}
                </select>
                {errors.attendance_type && <span className="form-error">{errors.attendance_type}</span>}
            </div>

            {/* Notes */}
            <div className="form-group">
                <label htmlFor="notes" className="form-label">
                    Notes (Optional)
                </label>
                <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    className="form-input"
                    rows="3"
                    placeholder="Add any notes about this attendance entry..."
                />
            </div>

            {/* Current Time Display */}
            <div className="time-display">
                <i className="fas fa-clock"></i>
                <span>Current Time: {new Date().toLocaleString()}</span>
            </div>

            {/* Form Actions */}
            <div className="form-actions">
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={onCancel}
                    disabled={loading}
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading || !formData.premises_id}
                >
                    {loading ? (
                        <>
                            <div className="spinner"></div>
                            Marking...
                        </>
                    ) : (
                        <>
                            <i className="fas fa-clock"></i>
                            Mark Attendance
                        </>
                    )}
                </button>
            </div>

            <style jsx>{`
        .mark-attendance-form {
          max-width: 100%;
        }

        .location-status {
          background: var(--light-color);
          padding: var(--spacing-lg);
          border-radius: var(--radius-lg);
          margin-bottom: var(--spacing-lg);
        }

        .location-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: var(--spacing-md);
        }

        .location-header h4 {
          margin: 0;
          color: var(--text-dark);
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
        }

        .location-loading {
          color: var(--info);
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          font-size: 0.9rem;
        }

        .location-error {
          color: var(--error);
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          font-size: 0.9rem;
        }

        .location-success {
          color: var(--success);
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          font-size: 0.9rem;
        }

        .form-group {
          margin-bottom: var(--spacing-lg);
        }

        .form-label {
          display: block;
          margin-bottom: var(--spacing-sm);
          font-weight: 600;
          color: var(--text-dark);
        }

        .required {
          color: var(--error);
        }

        .form-input {
          width: 100%;
          padding: 0.8rem;
          border: 2px solid #e0e0e0;
          border-radius: var(--radius-md);
          font-size: 1rem;
          transition: border-color var(--transition-normal);
          background: var(--white);
        }

        .form-input:focus {
          border-color: var(--admin-gradient);
          box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
        }

        .form-input.error {
          border-color: var(--error);
        }

        .form-input textarea {
          resize: vertical;
          min-height: 80px;
        }

        .form-error {
          color: var(--error);
          font-size: 0.8rem;
          margin-top: 0.25rem;
          display: block;
        }

        .time-display {
          background: rgba(52, 152, 219, 0.1);
          color: var(--info);
          padding: var(--spacing-md);
          border-radius: var(--radius-md);
          margin-bottom: var(--spacing-lg);
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          font-weight: 500;
        }

        .form-actions {
          display: flex;
          gap: var(--spacing-md);
          justify-content: flex-end;
          margin-top: var(--spacing-xl);
          padding-top: var(--spacing-lg);
          border-top: 1px solid var(--light-color);
        }

        @media (max-width: 768px) {
          .location-header {
            flex-direction: column;
            gap: var(--spacing-sm);
            align-items: stretch;
          }

          .form-actions {
            flex-direction: column-reverse;
          }

          .form-actions button {
            width: 100%;
          }
        }
      `}</style>
        </form>
    );
};

export default MarkAttendance;